import { useContext, useState, useEffect, useMemo } from "react";

import {
  Typography,
  useTheme,
  Container,
  Alert,
  Stack,
  Box,
  Divider,
} from "@mui/material";
import { getHours, isSameDay, isSameHour, isValid } from "date-fns";

import { AppContext } from "../../contextProviders/AppContext";
import rowlygo_logo_lila_rgb from "../../assets/rowlygo_logo_lila_rgb.png";
import { translation } from "../../components/Dictionary";
import SelectDate from "../../components/Datepicker/SelectDate";
import RentalServiceSelector from "./RentalServiceSelector";
import SelectTime from "../../components/Datepicker/SelectTime";
import UrentLogo from "../../components/UrentLogo";

export default function ServiceProvider() {
  const theme = useTheme();

  const {
    activeStep,
    reservationReturn,
    reservationStart,
    selectedCompany,
    mobileViewPort,
    language,
    setOffers,
    selectedDateRange,
    businessHourDates,
    setSelectedPicker,
    selectedPicker,
  } = useContext(AppContext);

  // These handle the new calendar component, values are transfered to dateRange, that checks restrictions

  // ID of Vuokraapaku Company Sörnäinen
  const VUOKRAAPAKU_SPECIAL_COMPANY = useMemo(() => {
    const VUOKRAAPAKU_COMPANY_ID = "00ef9a27-c9ea-4947-9115-436433a83a35";
    return selectedCompany?.id === VUOKRAAPAKU_COMPANY_ID;
  }, [selectedCompany]);

  const companyOpenHours = useMemo(() => {
    if (businessHourDates(selectedDateRange)) {
      const open = getHours(
        businessHourDates(selectedDateRange)?.start?.openingTime
      );
      const close = getHours(
        businessHourDates(selectedDateRange)?.start?.closingTime
      );

      const hoursArray = [];

      for (let hour = open; hour < close; hour++) {
        hoursArray.push(`${hour.toString().padStart(2, "0")}:00`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:15`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:30`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:45`);
      }

      // Add the 'close' time separately since it's the closing time (without half-hour)
      hoursArray.push(`${close.toString().padStart(2, "0")}:00`);
      return hoursArray;
    }
    return [];
  }, [businessHourDates, selectedDateRange]);

  // allow return times to any time of the day
  const companyOpenHoursReturn = useMemo(() => {
    if (businessHourDates(selectedDateRange)) {
      const open = getHours(
        businessHourDates(selectedDateRange)?.start?.openingTime
      );
      const close = 24;

      const hoursArray = [];

      for (let hour = open; hour < close; hour++) {
        hoursArray.push(`${hour.toString().padStart(2, "0")}:00`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:15`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:30`);
        hoursArray.push(`${hour.toString().padStart(2, "0")}:45`);
      }

      // Add the 'close' time separately since it's the closing time (without half-hour)
      hoursArray.push(`${close.toString().padStart(2, "0")}:00`);
      return hoursArray;
    }
    return [];
  }, [businessHourDates, selectedDateRange]);

  const [providerSelectOpen, setProviderSelectOpen] = useState(false);

  const searchButtonColor = useMemo(() => {
    if (!selectedDateRange[0] || !selectedDateRange[1]) {
      return theme.palette.warning.light;
    }

    if (!isValid(selectedDateRange[0] || !isValid(selectedDateRange[1]))) {
      return theme.palette.warning.light;
    }

    if (isValid(selectedDateRange[0] || isValid(selectedDateRange[1]))) {
      return theme.palette.success.main;
    }

    return theme.palette.primary.main;
  }, [
    selectedDateRange,
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.light,
  ]);
  // Control the heigth of the elements inside this container
  const contentHeigth = "56px";

  // close the calendar if provider select is open.
  useEffect(() => {
    if (providerSelectOpen) {
      setSelectedPicker(null);
    }
  }, [providerSelectOpen]);

  // open calendar when company is selected

  // check if dates from previous search and datepicker match
  // if not, customer is adviced to complete the new search
  const equalDates = useMemo(() => {
    // if there is no previous search, stay calm and keep on truckin
    if (activeStep > 2) return;
    if (!isValid(reservationStart)) return true;

    if (!isSameDay(reservationStart, selectedDateRange[0])) return false;
    if (!isSameDay(reservationReturn, selectedDateRange[1])) return false;
    if (!isSameHour(reservationStart, selectedDateRange[0])) return false;
    if (!isSameHour(reservationReturn, selectedDateRange[1])) return false;
    return true;
  }, [activeStep, selectedDateRange, reservationStart, reservationReturn]);

  const bgColor = useMemo(() => {
    if (selectedPicker || providerSelectOpen) {
      return theme.palette.secondary.serviceProviderBg;
    } else {
      return theme.palette.primary.serviceProviderBg;
    }
  }, [selectedPicker, providerSelectOpen, theme.palette]);

  const containerFullScreenStyles = {
    height: "60px",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "lightgrey",
    borderRadius: "45px",
    padding: "5px",
    marginTop: "20px",
    boxShadow: theme.shadows[0],
    bgcolor: bgColor,
    transition: "ease-in-out 250ms",
    display: "flex",
    flex: 1,
    width: "100%",
  };

  const containerMobileViewStyles = {
    borderRadius: "15px",
    padding: "5px",
    marginTop: "20px",
    boxShadow: theme.shadows[0],
    // bgcolor: "#f7f7f7",
    transition: "ease-in-out 150ms",
    display: "flex",
    flex: 1,
  };

  const searchButtonDesktopStyles = {
    minWidth: "50px",
    maxWidth: "150px",
    width: "100%",
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    fontWeight: "bolder",
    letterSpacing: 1,
    fontFamily: "Sofia Pro, sans serif",
    height: contentHeigth,
    /* width: "175px", */
    borderTopRightRadius: "45px",
    borderBottomRightRadius: " 45px",
    bgcolor: searchButtonColor,
    marginRight: "2px",
    cursor: "pointer",
    transition: "250ms ease-in-out",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  };

  const searchButtonMobileStyles = {
    width: "50%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    textTransform: "none",
    fontWeight: "bolder",
    letterSpacing: 1,
    fontFamily: "Sofia Pro, sans serif",
    height: "45px",
    /* width: "175px", */
    borderRadius: "45px",
    marginTop: "15px",
    marginBottom: "15px",
    bgcolor: searchButtonColor,
    boxShadow: theme.shadows[4],
    cursor: "pointer",
  };

  useEffect(() => {
    if (selectedCompany && !isValid(selectedDateRange[0])) {
      setSelectedPicker("calendar");
    }
  }, [selectedCompany, selectedDateRange, setOffers]);

  if (activeStep < 3)
    return (
      <>
        <Stack
          sx={{
            position: "relative",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: "98%",
              maxWidth: "1000px",
            }}
          >
            {!mobileViewPort && (
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                sx={
                  mobileViewPort
                    ? containerMobileViewStyles
                    : { ...containerFullScreenStyles }
                }
              >
                <RentalServiceSelector
                  setSelectedPicker={setSelectedPicker}
                  selectedPicker={selectedPicker}
                  contentHeigth={contentHeigth}
                  setProviderSelectOpen={setProviderSelectOpen}
                  providerSelectOpen={providerSelectOpen}
                />
                <SelectDate
                  contentHeigth={contentHeigth}
                  fullWidth={true}
                  startTime={selectedDateRange[0]}
                  endTime={selectedDateRange[1]}
                  companyOpenHours={companyOpenHours}
                  companyOpenHoursReturn={companyOpenHoursReturn}
                />
                <SelectTime
                  companyOpenHours={companyOpenHours}
                  companyOpenHoursReturn={companyOpenHoursReturn}
                />
                <SearchButton
                  searchButtonDesktopStyles={searchButtonDesktopStyles}
                  searchButtonMobileStyles={searchButtonMobileStyles}
                />
              </Stack>
            )}
            {mobileViewPort && (
              <Stack
                direction={"column"}
                sx={{
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                spacing={1}
              >
                <RentalServiceSelector
                  setSelectedPicker={setSelectedPicker}
                  selectedPicker={selectedPicker}
                  contentHeigth={contentHeigth}
                  setProviderSelectOpen={setProviderSelectOpen}
                  providerSelectOpen={providerSelectOpen}
                />
                <SelectDate
                  contentHeigth={contentHeigth}
                  fullWidth={true}
                  startTime={selectedDateRange[0]}
                  endTime={selectedDateRange[1]}
                  companyOpenHours={companyOpenHours}
                  companyOpenHoursReturn={companyOpenHoursReturn}
                />

                <SearchButton
                  searchButtonDesktopStyles={searchButtonDesktopStyles}
                  searchButtonMobileStyles={searchButtonMobileStyles}
                />
              </Stack>
            )}
          </Box>
          <Box
            display={"flex"}
            justifyContent={"flex-end"}
            sx={{
              marginX: { md: "8%", lg: "8%" },
              paddingRight: "20%",
              width: "100%",
            }}
          >
            <UrentLogo />
          </Box>
          {!equalDates && selectedPicker === null && activeStep === 2 && (
            <Box
              sx={{
                display: "flex",
                width: "90%",
                justifyContent: "center",
                top: "80px",
                position: !mobileViewPort && "absolute",
              }}
            >
              <Alert color="warning" sx={{ borderRadius: "15px" }}>
                <Typography>
                  {translation.get(language, "Search update text")}
                </Typography>
              </Alert>
            </Box>
          )}
        </Stack>
      </>
    );
}

function SearchButton(props) {
  const { searchButtonMobileStyles, searchButtonDesktopStyles } = props;
  const { language, setInitSearch, initSearch, mobileViewPort } =
    useContext(AppContext);
  const theme = useTheme();
  return (
    <Box
      sx={mobileViewPort ? searchButtonMobileStyles : searchButtonDesktopStyles}
      onClick={() => {
        if (!initSearch) {
          setInitSearch(true);
        }
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.primary.buttonText,
          fontSize: "20px",
        }}
      >
        {translation.get(language, "Search label")}
      </Typography>
    </Box>
  );
}
